<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import type { PaddingOptions } from 'mapbox-gl';
import { queryString } from '@/query';
import { useLocations } from '@/store';
import { campuses, campusAt, campusByCode, defaultCampus } from '@/data/campuses';
import { categoryByKey } from '@/data/categories';
import { doorOf, isTravelMode, originLngLat, type TravelMode } from '@/data/directions';
import type { Campus, LngLat, LocationType } from '@/data/types';
import { useDirections } from '@/useDirections';
import AppHeader from './AppHeader.vue';
import MapCanvas from './MapCanvas.vue';
import SearchBox from './SearchBox.vue';
import CategoryBrowser from './CategoryBrowser.vue';
import LocationList from './LocationList.vue';
import LocationDetails from './LocationDetails.vue';
import DirectionsPanel from './DirectionsPanel.vue';

const route = useRoute();
const router = useRouter();
const { status, error, locations, bySlug, searchIndex, countsByCategory, load } = useLocations();

onMounted(load);

// ---- State derived from the URL -------------------------------------------------------

const slug = computed(() => (typeof route.params.slug === 'string' ? route.params.slug.toLowerCase() : null));
const selected = computed(() => (slug.value ? (bySlug.value.get(slug.value) ?? null) : null));
const categoryKey = computed(() => queryString(route.query.category));
const activeCategory = computed(() => categoryByKey(selected.value?.category ?? categoryKey.value) ?? null);
const campusCode = computed(() => queryString(route.query.campus));
const travelMode = computed<TravelMode | null>(() => {
  const dir = queryString(route.query.dir);
  return isTravelMode(dir) ? dir : null;
});
const fromParam = computed(() => queryString(route.query.from));
/** ?acc=1: walk to the step-free entrance (only meaningful when the destination has one). */
const accessibleParam = computed(() => queryString(route.query.acc) === '1');

type View = 'loading' | 'error' | 'not-found' | 'directions' | 'details' | 'list' | 'home';
const view = computed<View>(() => {
  if (status.value === 'error') return 'error';
  if (status.value !== 'ready') return 'loading';
  if (slug.value && selected.value && travelMode.value) return 'directions';
  if (slug.value) return selected.value ? 'details' : 'not-found';
  if (activeCategory.value) return 'list';
  return 'home';
});

const categoryLocations = computed(() =>
  activeCategory.value ? locations.value.filter((l) => l.category === activeCategory.value!.key) : [],
);

// Buildings/Parking tab. Follows the open category so "back" lands on the right tab.
const browseType = ref<LocationType>('building');
watch(activeCategory, (c) => c && (browseType.value = c.type), { immediate: true });

// ---- Navigation ---------------------------------------------------------------------

function keepCampus(): Record<string, string> {
  return campusCode.value ? { campus: campusCode.value } : {};
}

function openLocation(s: string) {
  router.push({ name: 'map', params: { slug: s }, query: keepCampus() });
}

function openCategory(key: string) {
  router.push({ name: 'map', params: { slug: undefined }, query: { ...keepCampus(), category: key } });
}

function goHome() {
  router.push({ name: 'map', params: { slug: undefined }, query: keepCampus() });
}

function backFromDetails() {
  if (selected.value) openCategory(selected.value.category);
  else goHome();
}

// ---- Directions -----------------------------------------------------------------------
// /<destination-slug>?dir=walking&from=<origin-slug|here>

const useAccessibleEntrance = computed(
  () => accessibleParam.value && travelMode.value === 'walking' && !!selected.value?.accessibleEntrance,
);

const directions = useDirections(
  computed(() => (travelMode.value && selected.value ? doorOf(selected.value, useAccessibleEntrance.value) : null)),
  travelMode,
  fromParam,
  bySlug,
);

function directionsQuery(changes: { dir?: TravelMode; from?: string | null; acc?: boolean }) {
  const query: Record<string, string> = { ...keepCampus(), dir: changes.dir ?? travelMode.value ?? 'walking' };
  const from = changes.from === undefined ? fromParam.value : changes.from;
  if (from) query.from = from;
  if (changes.acc ?? accessibleParam.value) query.acc = '1';
  return query;
}

function openDirections() {
  if (!selected.value) return;
  const dir: TravelMode = selected.value.type === 'parking' ? 'driving' : 'walking';
  router.push({ name: 'map', params: { slug: selected.value.slug }, query: directionsQuery({ dir, from: null }) });
}

function updateDirections(changes: { dir?: TravelMode; from?: string | null; acc?: boolean }) {
  router.replace({ query: directionsQuery(changes) });
}

function useMyLocation() {
  // Already on from=here (e.g. after a failure): the URL won't change, so retry directly.
  if (fromParam.value === 'here') directions.retryLocate();
  else updateDirections({ from: 'here' });
}

function swapEnds() {
  const o = directions.origin.value;
  if (o?.kind !== 'location' || !selected.value) return;
  router.replace({
    name: 'map',
    params: { slug: o.location.slug },
    // The step-free choice belonged to the old destination.
    query: directionsQuery({ from: selected.value.slug, acc: false }),
  });
}

function closeDirections() {
  if (selected.value) router.push({ name: 'map', params: { slug: selected.value.slug }, query: keepCampus() });
}

const routeOrigin = computed(() => (view.value === 'directions' ? originLngLat(directions.origin.value) : null));
const shownRoute = computed(() => (view.value === 'directions' ? directions.route.value : null));

const shareUrl = computed(() => (selected.value ? new URL(router.resolve({ name: 'map', params: { slug: selected.value.slug } }).href, window.location.origin).href : ''));

// ---- Campus picker ------------------------------------------------------------------
// The pill shows whichever campus the camera is over; picking one flies there and records it
// in the URL (?campus=) so the view can be shared.

const initialCampus = campusByCode(campusCode.value) ?? defaultCampus;
const viewedCampus = ref<Campus | undefined>(initialCampus);

function onMapView(center: LngLat, zoom: number) {
  viewedCampus.value = campusAt(center, zoom);
}

const flyTarget = ref<{ center: LngLat; zoom: number; seq: number } | null>(null);
let flySeq = 0;

const campusSelect = computed({
  get: () => viewedCampus.value?.code ?? '',
  set: (code: string) => {
    const campus = campusByCode(code);
    if (!campus) return;
    // Fly directly rather than via a route watcher, so re-picking the campus already in the
    // URL (after panning away) still moves the camera.
    flyTarget.value = { center: campus.center, zoom: campus.zoom, seq: ++flySeq };
    viewedCampus.value = campus;
    router.replace({ query: { ...route.query, campus: code } });
  },
});

// ---- Page title ---------------------------------------------------------------------

watch(
  [selected, activeCategory, travelMode],
  () => {
    const base = 'University of Rochester: Explore Maps';
    document.title = selected.value
      ? `${travelMode.value ? 'Directions to ' : ''}${selected.value.title} | ${base}`
      : activeCategory.value
        ? `${activeCategory.value.label} | ${base}`
        : base;
  },
  { immediate: true },
);

// ---- Panel layout (desktop side panel / mobile bottom sheet) ------------------------

const panel = ref<HTMLElement>();
const panelBody = ref<HTMLElement>();
const sheetOpen = ref(true);
const isDesktop = ref(true);
const padding = reactive<PaddingOptions>({ top: 0, bottom: 0, left: 0, right: 0 });

const mql = window.matchMedia('(min-width: 768px)');
function syncLayout() {
  isDesktop.value = mql.matches;
  const el = panel.value;
  if (!el) return;
  const rect = el.getBoundingClientRect();
  const mapRect = el.parentElement!.getBoundingClientRect();
  if (isDesktop.value) {
    Object.assign(padding, { top: 0, bottom: 0, right: 0, left: Math.round(rect.right - mapRect.left) });
  } else {
    Object.assign(padding, { top: 0, left: 0, right: 0, bottom: Math.round(mapRect.bottom - rect.top) });
  }
}

let resizeObserver: ResizeObserver | undefined;
onMounted(() => {
  mql.addEventListener('change', syncLayout);
  window.addEventListener('resize', syncLayout); // backstop where ResizeObserver lags
  resizeObserver = new ResizeObserver(syncLayout);
  if (panel.value) resizeObserver.observe(panel.value);
  syncLayout();
});
watch(sheetOpen, () => nextTick(syncLayout));
onBeforeUnmount(() => {
  mql.removeEventListener('change', syncLayout);
  window.removeEventListener('resize', syncLayout);
  resizeObserver?.disconnect();
});

// Show the sheet and reset scroll whenever the panel changes screens; move focus to the new
// heading for keyboard and screen reader users (but not on first load).
let firstView = true;
watch([view, slug, categoryKey], async () => {
  sheetOpen.value = true;
  await nextTick();
  syncLayout(); // panel height changed; ResizeObserver alone isn't reliable in every browser
  panelBody.value?.scrollTo({ top: 0 });
  if (!firstView) panelBody.value?.querySelector<HTMLElement>('h2')?.setAttribute('tabindex', '-1');
  if (!firstView) panelBody.value?.querySelector<HTMLElement>('h2')?.focus({ preventScroll: true });
  firstView = false;
});

// Phones: tapping empty map collapses the sheet so the map can be seen.
function onMapBackgroundClick() {
  if (!isDesktop.value) sheetOpen.value = false;
}

// Phones: swipe the sheet's grab strip down to collapse, up to expand.
let swipeStartY: number | null = null;
function onHandlePointerDown(e: PointerEvent) {
  swipeStartY = e.clientY;
}
function onHandlePointerUp(e: PointerEvent) {
  if (swipeStartY === null) return;
  const dy = e.clientY - swipeStartY;
  swipeStartY = null;
  if (dy > 30) sheetOpen.value = false;
  else if (dy < -30) sheetOpen.value = true;
  else sheetOpen.value = !sheetOpen.value; // a tap toggles
}

function onKeydown(e: KeyboardEvent) {
  if (e.key !== 'Escape' || e.defaultPrevented) return;
  if (view.value === 'directions') closeDirections();
  else if (view.value === 'details' || view.value === 'not-found') backFromDetails();
  else if (view.value === 'list') goHome();
}
</script>

<template>
  <div class="app" @keydown="onKeydown">
    <a class="skip-link" href="#panel-body">Skip to location list</a>
    <AppHeader />

    <main
      class="stage"
      :style="{ '--map-inset-left': `${padding.left ?? 0}px`, '--map-inset-bottom': `${padding.bottom ?? 0}px` }"
    >
      <MapCanvas
        :locations="locations"
        :active-category="activeCategory?.key ?? null"
        :selected="selected"
        :padding="padding"
        :fly-target="flyTarget"
        :initial-center="initialCampus.center"
        :initial-zoom="initialCampus.zoom"
        :route="shownRoute"
        :route-origin="routeOrigin"
        :routing="view === 'directions'"
        @select="openLocation"
        @view="onMapView"
        @background-click="onMapBackgroundClick"
      />

      <aside ref="panel" class="panel" :class="{ 'is-collapsed': !isDesktop && !sheetOpen }" aria-label="Find a location">
        <div class="panel__top">
          <button
            v-if="!isDesktop"
            type="button"
            class="panel__handle"
            :aria-expanded="sheetOpen"
            aria-controls="panel-body"
            @pointerdown="onHandlePointerDown"
            @pointerup="onHandlePointerUp"
            @pointercancel="swipeStartY = null"
            @keydown.enter.prevent="sheetOpen = !sheetOpen"
            @keydown.space.prevent="sheetOpen = !sheetOpen"
          >
            <span class="visually-hidden">{{ sheetOpen ? 'Collapse panel' : 'Expand panel' }}</span>
            <svg
              class="panel__chevron"
              :class="{ 'is-up': !sheetOpen }"
              viewBox="0 0 512 512"
              width="14"
              height="14"
              aria-hidden="true"
            >
              <path
                fill="currentColor"
                d="M233.4 406.6a32 32 0 0 0 45.3 0l192-192a32 32 0 0 0-45.3-45.3L256 338.7 86.6 169.4a32 32 0 0 0-45.3 45.3l192 192z"
              />
            </svg>
          </button>
          <SearchBox :index="searchIndex" :disabled="status !== 'ready'" @select="openLocation" />
          <div class="campus">
            <svg class="campus__pin" viewBox="0 0 384 512" width="12" height="16" aria-hidden="true">
              <path
                fill="currentColor"
                d="M215.7 499.2C267 435 384 279.4 384 192 384 86 298 0 192 0S0 86 0 192c0 87.4 117 243 168.3 307.2a30.8 30.8 0 0 0 47.4 0zM192 128a64 64 0 1 1 0 128 64 64 0 1 1 0-128z"
              />
            </svg>
            <select v-model="campusSelect" class="campus__select" aria-label="Jump to a campus">
              <option value="" disabled>Jump to a campus</option>
              <option v-for="c in campuses" :key="c.code" :value="c.code">{{ c.name }}</option>
            </select>
            <svg class="campus__chevron" viewBox="0 0 512 512" width="10" height="10" aria-hidden="true">
              <path
                fill="currentColor"
                d="M233.4 406.6a32 32 0 0 0 45.3 0l192-192a32 32 0 0 0-45.3-45.3L256 338.7 86.6 169.4a32 32 0 0 0-45.3 45.3l192 192z"
              />
            </svg>
          </div>
        </div>

        <div v-show="isDesktop || sheetOpen" id="panel-body" ref="panelBody" class="panel__body" tabindex="-1">
          <p v-if="view === 'loading'" class="notice" role="status">Loading locations…</p>

          <div v-else-if="view === 'error'" class="notice notice--error" role="alert">
            <p>We couldn’t load campus locations.</p>
            <p class="notice__detail">{{ error }}</p>
            <button type="button" class="retry" @click="load">Try again</button>
          </div>

          <div v-else-if="view === 'not-found'" class="notice" role="alert">
            <p>We couldn’t find a location called “{{ slug }}”.</p>
            <button type="button" class="retry" @click="goHome">Browse all locations</button>
          </div>

          <DirectionsPanel
            v-else-if="view === 'directions' && selected && travelMode"
            :destination="selected"
            :origin="directions.origin.value"
            :mode="travelMode"
            :accessible="useAccessibleEntrance"
            :route="directions.route.value"
            :route-status="directions.routeStatus.value"
            :route-error="directions.routeError.value"
            :search-index="searchIndex"
            @back="closeDirections"
            @pick-origin="(s) => updateDirections({ from: s })"
            @use-my-location="useMyLocation"
            @clear-origin="updateDirections({ from: null })"
            @swap="swapEnds"
            @set-mode="(m) => updateDirections({ dir: m })"
            @set-accessible="(on) => updateDirections({ acc: on })"
          />

          <LocationDetails
            v-else-if="view === 'details' && selected"
            :location="selected"
            :share-url="shareUrl"
            @back="backFromDetails"
            @directions="openDirections"
          />

          <LocationList
            v-else-if="view === 'list' && activeCategory"
            :category="activeCategory"
            :locations="categoryLocations"
            @back="goHome"
            @select="openLocation"
          />

          <CategoryBrowser v-else v-model:type="browseType" :counts="countsByCategory" @open="openCategory" />
        </div>
      </aside>
    </main>
  </div>
</template>

<style scoped>
.app {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.skip-link {
  position: absolute;
  z-index: 100;
  left: 8px;
  top: -48px;
  padding: 8px 12px;
  background: var(--ur-blue);
  color: #fff;
  border-radius: var(--radius);
}

.skip-link:focus {
  top: 8px;
}

.stage {
  position: relative;
  flex: 1;
  min-height: 0;
}

.panel {
  position: absolute;
  z-index: 2;
  display: flex;
  flex-direction: column;
  background: var(--surface);
  box-shadow: var(--shadow);
}

.panel__top {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
  background: var(--ur-blue);
  border-bottom: 3px solid var(--interactive-blue);
}

.panel__body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 12px;
}

.panel__body:focus {
  box-shadow: none;
}

/* Campus pill: a native <select> (accessible, good on phones) dressed as a pill. */
.campus {
  position: relative;
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  max-width: 100%;
  color: #fff;
}

.campus__pin,
.campus__chevron {
  position: absolute;
  pointer-events: none;
}

.campus__pin {
  left: 12px;
}

.campus__chevron {
  right: 12px;
  opacity: 0.8;
}

.campus__select {
  appearance: none;
  -webkit-appearance: none;
  field-sizing: content; /* shrink to the chosen option where supported */
  max-width: 100%;
  height: 32px;
  padding: 0 30px 0 30px;
  border: 1px solid rgb(255 255 255 / 0.45);
  border-radius: 999px;
  background: rgb(255 255 255 / 0.08);
  color: inherit;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  text-overflow: ellipsis;
}

.campus__select:hover {
  background: rgb(255 255 255 / 0.16);
  border-color: rgb(255 255 255 / 0.7);
}

.campus__select option {
  color: var(--text);
  background: var(--surface);
}

.notice {
  margin: 8px 0;
  color: var(--text-muted);
}

.notice p {
  margin: 0 0 8px;
}

.notice--error {
  color: var(--danger);
}

.notice__detail {
  font-size: 13px;
  color: var(--text-muted);
}

.retry {
  height: 36px;
  padding: 0 14px;
  border: 1px solid var(--interactive-blue);
  border-radius: var(--radius);
  background: var(--surface);
  color: var(--interactive-blue);
  font-weight: 700;
}

/* Desktop: floating side panel */
@media (min-width: 768px) {
  .panel {
    top: 16px;
    left: 16px;
    bottom: 16px;
    width: var(--panel-width);
    max-height: calc(100% - 32px);
    height: fit-content;
    border-radius: var(--radius);
  }

  /* No overflow:hidden on .panel: the search results dropdown must be able to extend past it. */
  .panel__top {
    border-radius: var(--radius) var(--radius) 0 0;
  }
}

/* Mobile: bottom sheet */
@media (max-width: 767px) {
  .panel {
    left: 0;
    right: 0;
    bottom: 0;
    max-height: 62%;
    border-radius: 14px 14px 0 0;
    padding-bottom: env(safe-area-inset-bottom);
  }

  .panel__top {
    padding-top: 32px;
    border-radius: 14px 14px 0 0;
  }

  /* The whole top strip is the tap/swipe target (not just the little bar). */
  .panel__handle {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 32px;
    border: 0;
    border-radius: 14px 14px 0 0;
    background: none;
    color: rgb(255 255 255 / 0.8);
    touch-action: none; /* let us see the swipe instead of the page scrolling */
  }

  .panel__handle::before {
    content: '';
    position: absolute;
    left: 50%;
    top: 12px;
    width: 40px;
    height: 5px;
    translate: -50% 0;
    border-radius: 3px;
    background: rgb(255 255 255 / 0.55);
  }

  .panel__chevron {
    position: absolute;
    right: 16px;
    top: 9px;
    transition: rotate 0.2s;
  }

  .panel__chevron.is-up {
    rotate: 180deg;
  }

  .panel.is-collapsed .panel__top {
    border-bottom: 0;
  }
}
</style>
